<script lang="ts">
	import { getVariantName } from '$lib/util/ecom/util';
	import { formatPrice } from '$lib/util/ecom/format';
	import Text from '../Text.svelte';
	import GlobalMedia from '../media/GlobalMedia.svelte';
	import {
		cartOpen,
		closeCart,
		removeFromCart,
		cartChanged,
		addToCart,
		checkout,
		updateCartItemQuantity,
		optimisticCart,
		applyDiscountCodes
	} from '$lib/stores/shopStore';
	import Button from '../util/Button.svelte';
	import QuantitySelector from './QuantitySelector.svelte';
	import { page } from '$app/stores';
	import Link from '$lib/components/navigation/Link.svelte';
	import Media from '../media/Media.svelte';
	import { EU_COUNTRIES } from '$lib/util/ecom/countries';
	import Loader from '../util/Loader.svelte';
	import { injectDiscounts } from '$lib/util/ecom/discounts';

	// $: filteredCart = $cart.filter((item) => item.type === $page.data.customerType) ?? [];
	// $: cartTotal = filteredCart.map((item) => item.price * item.amount).reduce((a, b) => a + b, 0);

	$: cartTotal = Number(cart?.cost?.totalAmount?.amount);

	$: cart = $optimisticCart;

	$: cartOpen.subscribe((value) => {
		if (typeof document == 'undefined' || !document.body?.style) return;
		document.body.style.overflow = value === true ? 'hidden' : 'auto';
	});

	const freeShippingPrice: number = 50;
	$: differnceToFreeShipping = freeShippingPrice - cartTotal;
	// $: freeShippingPercentage = Math.min(100, (cartTotal / freeShippingPrice) * 100);

	let isFreeShipping = false;
	let showShippingBar = true;

	$: {
		if ($page.data.location === 'AT') {
			isFreeShipping = differnceToFreeShipping <= 0;
			showShippingBar = true;
		} else {
			isFreeShipping = false;
			showShippingBar = false;
		}
	}

	$: recommendationProducts = $page.data.recommendations?.collection?.products?.edges ?? [];
	$: filteredRecommendationProducts = recommendationProducts.filter(
		(item: any) => item.node?.product_type?.value === $page.data.customerType
	);

	let checkoutState: 'idle' | 'loading' | 'error' = 'idle';

	$: checkoutDisabled = cart?.lines?.edges?.length === 0;

	async function handleCheckout() {
		checkoutState = 'loading';

		window.addEventListener('unload', function (e) {
			checkoutState = 'idle';
		});

		await checkout().catch((err) => {
			checkoutState = 'error';
		});
	}
	let recommendationContainer: HTMLElement;
	function scrollLeft() {
		recommendationContainer.scrollBy({
			left: -300,
			behavior: 'smooth'
		});
	}

	function scrollRight() {
		recommendationContainer.scrollBy({
			left: 300,
			behavior: 'smooth'
		});
	}
</script>

<div class="cart-wrapper" class:open={$cartOpen} on:click|self={closeCart}>
	<div class="cart">
		<div class="cart-top">
			<div class="title-row">
				<div class="title"><Text key="ecom_cart__title" /></div>
				<div class="close" on:click={closeCart}>
					<GlobalMedia key="icon-close" />
				</div>
			</div>
			{#if showShippingBar}
				<div class="shipping-indicator">
					<!-- <div class="progress-bar">
						<div class="bar" style="width: {freeShippingPercentage}%" />
					</div> -->
					<div class="notice">
						{#if isFreeShipping}
							<Text key="ecom_cart__free_shipping" />
						{:else}
							<Text
								key="ecom_cart__free_shipping_limit"
								params={{
									total: formatPrice(
										freeShippingPrice,
										$page.data.locationCurrency,
										$page.params.lang
									),
									difference: formatPrice(
										differnceToFreeShipping,
										$page.data.locationCurrency,
										$page.params.lang
									)
								}}
							/>
						{/if}
					</div>
				</div>
			{/if}
		</div>
		<div class="cart-content">
			<div class="cart-items">
				{#each cart?.lines?.edges ?? [] as { node: item }}
					<div class="cart-item">
						<div class="image-wrapper" on:click={closeCart}>
							<Link type="internal" link="shop/{item?.merchandise?.product?.handle}">
								<img
									src={item?.merchandise?.image?.url}
									alt={item?.merchandise?.image?.altText ?? ''}
									loading="lazy"
								/>
							</Link>
						</div>
						<div class="content">
							<div class="infos">
								<div class="title" on:click={closeCart}>
									<Link type="internal" link="shop/{item?.merchandise?.product?.handle}">
										{item?.merchandise?.product?.title}
									</Link>
								</div>
								{#if item?.isOptimistic && !item?.cost?.amountPerQuantity?.amount}
									<Loader />
								{:else}
									<div class="info-group">
										<div class="variant">
											{item?.merchandise?.selectedOptions?.map((i) => i.value).join(', ') ?? ''}
										</div>
										{#if item?.cost?.compareAtAmountPerQuantity}
											<div class="price discount-price">
												{formatPrice(
													injectDiscounts(item?.cost?.compareAtAmountPerQuantity.amount),
													item?.cost?.compareAtAmountPerQuantity.currencyCode,
													$page.params.lang
												)}
											</div>
										{/if}
										<div class="price">
											{formatPrice(
												injectDiscounts(item?.cost?.amountPerQuantity?.amount),
												item?.cost?.amountPerQuantity?.currencyCode,
												$page.params.lang
											)}
										</div>
									</div>
								{/if}
								{#if item?.sellingPlanAllocation?.sellingPlan?.name}
									<div class="subscription-info">
										{item?.sellingPlanAllocation?.sellingPlan?.name}
									</div>
								{/if}

								<!-- {#if item?.sellingPlanAllocation?.sellingPlan?.deliveryPolicy?.intervalCount}
									<div class="subscription-info">
										{item?.sellingPlanAllocation.sellingPlan.deliveryPolicy.intervalCount}
										<Text
											tag="span"
											key={'ecom_' +
												item?.sellingPlanAllocation.sellingPlan.deliveryPolicy.interval.toLowerCase() +
												(item?.sellingPlanAllocation.sellingPlan.deliveryPolicy.intervalCount > 1
													? 's'
													: '')}
										/>
										<Text tag="span" key="ecom_subscription" />
									</div>
								{/if} -->
							</div>
							<div class="tools">
								<div class="remove" on:click={() => removeFromCart(item?.id)}>
									<GlobalMedia key="icon-bin" />
								</div>
								<div class="quantity">
									<!-- TODO: max value -->
									<QuantitySelector
										min={1}
										max={1000}
										value={item?.quantity ?? 0}
										on:change={(e) => updateCartItemQuantity(item?.id, e.detail)}
										variant="small"
									/>
								</div>
								<div class="total-price">
									{formatPrice(
										item?.cost?.totalAmount?.amount,
										item?.cost?.totalAmount?.currencyCode,
										$page.params.lang
									)}
								</div>
							</div>
						</div>
					</div>
				{/each}
				{#if !cart?.lines?.edges?.length}
					<div class="empty">
						<Text key="ecom_cart__empty" />
					</div>
				{/if}
			</div>
			{#if filteredRecommendationProducts?.length > 0}
				<div class="recommendations">
					<div class="title">
						<Text key="ecom_cart__bestsellers__title" />
						<div class="arrows">
							<div on:click={scrollLeft}><GlobalMedia key="icon-arrow-left" /></div>
							<div on:click={scrollRight}><GlobalMedia key="icon-arrow-right" /></div>
						</div>
					</div>
					<div class="recommendation-items" bind:this={recommendationContainer}>
						{#each filteredRecommendationProducts as item}
							<div class="recommendation-item">
								<div class="image-wrapper" on:click={closeCart}>
									<Link type="internal" link="shop/{item?.node?.handle}">
										<img
											src={item.node?.variants?.edges?.[0]?.node?.image?.url}
											alt=""
											loading="lazy"
										/>
									</Link>
								</div>
								<div class="content">
									<div class="title" on:click={closeCart}>
										<Link type="internal" link="shop/{item?.node?.handle}">
											{item.node?.title}
										</Link>
									</div>
									<div class="info-row">
										<div class="variant">
											{getVariantName(item.node?.variants?.edges?.[0]?.node) ?? ''}
										</div>
										<div class="price">
											{formatPrice(
												injectDiscounts(item.node?.variants?.edges?.[0]?.node?.priceV2?.amount),
												item.node?.variants?.edges?.[0]?.node?.priceV2?.currencyCode,
												$page.params.lang
											)}
										</div>
									</div>

									<div class="add">
										<Button
											size="small"
											style="yellow"
											on:click={() =>
												addToCart(
													item.node?.variants?.edges?.[0]?.node?.id,
													1,
													item.node?.variants?.edges?.[0]?.node?.priceV2?.amount,
													item.node?.variants?.edges?.[0]?.node?.priceV2?.currencyCode,
													item?.node?.title,
													item.node?.variants?.edges?.[0]?.node?.image?.url
												)}
											iconKey="icon-plus"
											textKey="ecom_cart__bestsellers__add"
										/>
									</div>
								</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}
		</div>
		<div class="cart-bottom">
			<Button
				textKey="ecom_cart__checkout"
				size="large"
				class="checkout-button"
				loading={checkoutState === 'loading'}
				disabled={checkoutDisabled || checkoutState === 'loading'}
				on:click={() => !checkoutDisabled && handleCheckout()}
			>
				<div slot="custom_after">
					{#if checkoutState !== 'loading'}
						<div class="price" class:changed={$cartChanged}>
							{formatPrice(
								cart?.cost?.subtotalAmount?.amount,
								cart?.cost?.subtotalAmount?.currencyCode,
								$page.params.lang
							)}
						</div>
					{:else}
						<Loader />
					{/if}
				</div>
			</Button>
			<!-- <Button on:click={() => applyDiscountCodes(['DISCOUNT_10'])} display_text="add discount" /> -->

			<div class="notice">
				<Text key="ecom_cart__notice" />
			</div>
			<div class="payment-methods">
				{#each $page.data.globals.payment_methods ?? [] as { directus_files_id: item }}
					<div class="payment-method">
						<Media {...item} />
					</div>
				{/each}
			</div>
		</div>
	</div>
</div>

<style lang="scss">
	.cart-wrapper {
		position: fixed;
		inset: 0;
		width: 100%;
		height: 100%;
		background-color: color(modal__backdrop);
		z-index: v(z-index-cart);
		@include transition(all);
		cursor: pointer;

		&:not(.open) {
			background-color: color(modal__backdrop__hidden);
			pointer-events: none;

			.cart {
				transform: translateX(100%);
			}
		}
	}

	.cart {
		position: absolute;
		height: 100%;
		background-color: color(grey_01);
		right: 0;
		width: 100%;
		max-width: 550px;
		padding: v(content-padding-sm);
		cursor: default;

		@include media(phone) {
			max-width: none;
		}

		display: flex;
		flex-direction: column;
		@include transition(all);
	}

	.payment-methods {
		width: auto;
		display: flex;
		gap: v(content-gap-2xs);
		justify-content: center;

		.payment-method {
			border-radius: v(border-radius-sm);
			background-color: color(white);
			display: flex;
			align-items: center;
			:global(svg),
			:global(img) {
				width: 100%;
				height: auto;
				max-height: 2rem;
				@include media(tablet) {
					max-height: 1.8rem;
					max-width: 30px;
				}
			}
		}
		@include media(phone) {
			justify-content: flex-start;
			flex-wrap: wrap;
		}
	}

	.recommendations {
		display: flex;
		padding: v(content-gap-sm);
		flex-direction: column;
		gap: v(content-gap-xs);
		background: color(yellow_02);
		border-radius: v(border-radius);
		position: sticky;
		bottom: 0;
		.arrows {
			display: flex;
			gap: v(content-gap-xs);
			:global(svg) {
				cursor: pointer;
			}
		}

		.title {
			@include font(h6);
			color: color(warm-stone);
			text-transform: uppercase;
			display: flex;
			justify-content: space-between;
		}

		.recommendation-items {
			display: flex;
			overflow-x: auto;
			margin-inline-end: calc(-1 * v(content-gap-sm));
			padding-inline-end: v(content-gap-sm);
			gap: v(content-gap-sm);
			padding-bottom: v(content-gap-sm);

			.recommendation-item {
				flex-shrink: 0;
				display: grid;
				grid-template-columns: 1fr 1fr;
				width: 250px;
				gap: v(content-gap-2xs);

				@include media(phone) {
					width: 200px;
				}

				.image-wrapper {
					aspect-ratio: 1/1;
					background-color: color(white);
					@include transition(all);
					:global(a) {
						display: flex;
					}

					&:hover {
						opacity: 0.7;
					}

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}
				}

				.content {
					display: flex;
					flex-direction: column;
					padding-block: v(content-padding-xs);
				}

				.info-row {
					flex: 1;
					@include font(text-2xs);

					display: flex;
					justify-content: space-between;
				}

				.variant {
				}

				.title {
					@include font(h6);
					color: inherit;
					text-decoration: none;
					@include transition(all);

					:global(a) {
						color: color(black);
					}

					&:hover {
						opacity: 0.3;
					}
				}
			}
		}
	}

	.title-row {
		display: flex;
		justify-content: space-between;

		// border-bottom: 1px solid color(black);
		padding-bottom: v(content-gap-xs);

		.title {
			@include font(h3);
		}

		.close {
			cursor: pointer;
			@include transition(all);

			&:hover {
				opacity: 0.3;
			}

			:global(svg) {
				width: 1rem;

				:global(path) {
					fill: color(dark);
				}
			}
		}
	}

	.shipping-indicator {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-xs);

		// .progress-bar {
		// 	height: 0.5rem;
		// 	border-radius: 0.25rem;
		// 	z-index: 1;
		// 	position: relative;
		// 	background-color: color(white);

		// 	.bar {
		// 		content: '';
		// 		display: block;
		// 		height: 100%;
		// 		background-color: color(warm-stone);
		// 		border-radius: 0.25rem;
		// 		position: absolute;
		// 		@include transition(all);
		// 	}
		// }

		.notice {
			@include font(text-sm);
			color: color(warm-stone);
			:global(span) {
				font-weight: bold;
			}
		}
	}

	.cart-content {
		flex: 1;
		overflow-y: auto;
		margin-inline: calc(-1 * v(content-gap));
		padding-inline: v(content-gap);

		display: flex;
		flex-direction: column;
		gap: v(content-gap-xxl);
		overflow: auto;
	}

	.cart-top {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-sm);
		padding-bottom: v(content-gap-sm);
		border-bottom: 1px solid color(grey_04);
	}

	.cart-bottom {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-sm);
		padding-top: v(content-gap-sm);

		:global(.checkout-button) {
			width: 100%;
			display: flex;
			justify-content: space-between !important;
			padding-inline: v(content-gap) !important;

			:global(.price) {
				line-height: 100%;
			}

			:global(.price.changed) {
				animation: bounce 0.5s;
			}

			:global(.loader-wrapper) {
				--color: var(--color-primary) !important;
				--size: 0.8em !important;
				--width: 0.15em !important;
				display: flex;
				justify-content: center;
				align-items: center;
				margin-top: 0;
			}
		}

		:global(.checkout-button.loading) {
			justify-content: center !important;

			:global(.price) {
				display: none;
			}
		}

		.notice {
			@include font(text-sm);
			color: color(grey_06);
			text-align: left;
		}
	}

	.cart-items {
		display: flex;
		flex-direction: column;
		flex: 1;

		.empty {
			text-align: center;
			font-style: italic;
			margin-top: v(content-gap-xl);
		}
	}

	.cart-item {
		display: grid;
		grid-template-columns: clamp(50px, 33%, 200px) auto;
		gap: v(content-gap);

		border-bottom: 1px solid color(grey_04);
		padding-block: v(content-gap-sm);

		:global(.loader-wrapper) {
			--color: var(--color-primary) !important;
			--size: 1.2em !important;
			--width: 0.2em !important;
			display: flex;
			justify-content: center;
			align-items: center;
			margin-top: 0;
		}

		.image-wrapper {
			aspect-ratio: 1;
			@include transition(all);
			width: 100%;

			a {
				background-color: color(white);
			}

			&:hover {
				opacity: 0.7;
			}

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			gap: v(content-gap-sm);

			.infos {
				display: flex;
				flex-direction: column;
				gap: v(content-gap-sm);

				.info-group {
					display: flex;
					gap: v(content-gap);
				}

				.subscription-info {
					@include font(text-sm);
				}

				.title {
					@include font(h5);
					text-decoration: none;
					@include transition(all);

					:global(a) {
						color: color(black);
					}

					&:hover {
						opacity: 0.3;
					}
				}

				.variant,
				.price {
					@include font(text-sm);
					color: color(warm-stone);
				}

				.discount-price {
					text-decoration: line-through;
				}
			}

			.tools {
				display: flex;
				align-items: center;
				gap: v(content-gap-lg);
				flex-wrap: wrap;

				.total-price {
					@include font(h6);
				}

				.remove {
					cursor: pointer;
					@include transition(all);

					:global(.svg) {
						height: 1rem;
						:global(path) {
							fill: color(grey_06);
						}
					}

					&:hover {
						opacity: 0.5;
					}
				}
			}
		}
	}
</style>
